import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PacienteFormModal from "../components/paciente/PacienteFormModal";
import HistoriaClinicaImpresion from "../components/HistoriaClinicaImpresion";
import EnviarAccesoModal from "../components/paciente/EnviarAccesoModal";
import { API, authHeaders } from "../services/config";
import "./PacientesPage.css";

const BUSQUEDA_STORAGE_KEY = "pacientes.busqueda";
const SCROLL_STORAGE_KEY = "pacientes.scrollY";

function formatDate(dateValue) {
  if (!dateValue) return "-";
  const s = String(dateValue);
  const date = new Date(s.includes("T") ? s : `${s}T00:00:00`);
  return Number.isNaN(date.getTime()) ? dateValue : date.toLocaleDateString("es-AR");
}

function truncate(text = "", max = 44) {
  if (!text || text.length <= max) return text || "-";
  return `${text.slice(0, max).trim()}...`;
}

export default function PacientesPage() {
  const [pacientes, setPacientes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPaciente, setModalPaciente] = useState(null);
  const [isNewPatient, setIsNewPatient] = useState(true);
  const [busqueda, setBusqueda] = useState(() => (sessionStorage.getItem(BUSQUEDA_STORAGE_KEY) || "").trim());
  const [impresionPacienteId, setImpresionPacienteId] = useState(null);

  const [modalAccesoOpen, setModalAccesoOpen] = useState(false);
  const [modalAccesoPaciente, setModalAccesoPaciente] = useState(null);
  const [enviandoAcceso, setEnviandoAcceso] = useState(false);

  const navigate = useNavigate();

  const fetchPacientes = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API}/pacientes`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) navigate("/login");
        throw new Error("Error al cargar pacientes");
      }

      const data = await response.json();

      if (data.success && data.data) {
        const lista = Array.isArray(data.data) ? data.data : Object.values(data.data || {});
        setPacientes(lista);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPacientes();
  }, [fetchPacientes]);

  useEffect(() => {
    sessionStorage.setItem(BUSQUEDA_STORAGE_KEY, busqueda);
  }, [busqueda]);

  useEffect(() => {
    const savedScroll = Number(sessionStorage.getItem(SCROLL_STORAGE_KEY) || 0);
    if (savedScroll > 0) {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, savedScroll);
      });
    }

    return () => {
      sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY || 0));
    };
  }, []);

  const handleEditPaciente = (paciente) => {
    setIsNewPatient(false);
    setModalPaciente(paciente);
    setModalOpen(true);
  };

  const handleAddPaciente = () => {
    setIsNewPatient(true);
    setModalPaciente(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalPaciente(null);
  };

  const handleEnviarAcceso = async () => {
    if (!modalAccesoPaciente) return;
    setEnviandoAcceso(true);
    try {
      const resp = await fetch(`${API}/pacientes/${modalAccesoPaciente.id}/enviar-acceso-portal`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        alert(`✅ WhatsApp encolado para ${modalAccesoPaciente.nombre}. Se enviará en breve.`);
        setModalAccesoOpen(false);
      } else {
        const detalle = data.password_generado
          ? `\n\nContraseña generada: ${data.password_generado}`
          : '';
        alert(`❌ ${data.message || 'No se pudo enviar el acceso'}${detalle}`);
      }
    } catch {
      alert('❌ Error de conexión');
    } finally {
      setEnviandoAcceso(false);
    }
  };

  const termino = busqueda.trim().toLowerCase();

  const pacientesFiltrados = pacientes.filter(
    (p) =>
      termino === "" ||
      (p.nombre && p.nombre.toLowerCase().includes(termino)) ||
      (p.telefono && p.telefono.toLowerCase().includes(termino)) ||
      (p.apellido && p.apellido.toLowerCase().includes(termino)) ||
      (p.dni && String(p.dni).includes(termino))
  );

  return (
    <div className="pp-container">
      <Sidebar />
      <div className="pp-content">
        <h2 className="pp-title">Pacientes</h2>

        <div className="pp-busqueda-container">
          <div className="pp-busqueda-wrapper">
            <span className="pp-busqueda-icon">🔍</span>
            <input
              type="text"
              placeholder="Buscar por nombre, apellido o DNI..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pp-busqueda-input"
            />
          </div>

          <button type="button" onClick={handleAddPaciente} className="pp-btn-agregar">
            <span className="pp-btn-icon">+</span> Agregar Paciente
          </button>
        </div>

        <ul className="pp-lista">
          {pacientesFiltrados.map((p, i) => {
            const totalCosto = parseFloat(p.total_costo || 0);
            const totalAbonado = parseFloat(p.total_abonado || 0);
            const balance = totalAbonado - totalCosto;
            const estaAlDia = balance >= -0.1;

            return (
              <li
                key={p.id || i}
                className="pp-card"
                onClick={(e) => {
                  if (e.target.closest("button") || e.target.closest(".pp-card-nombre")) return;
                  handleEditPaciente(p);
                }}
                role="presentation"
              >
                <div className="pp-card-header">
                  <h3
                    className="pp-card-nombre"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPaciente(p);
                    }}
                    style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
                    title="Editar datos del paciente"
                  >
                    {p.nombre} {p.apellido}
                    <span style={{ fontSize: "0.8em", opacity: 0.7 }}>✏️</span>
                  </h3>

                  <div className="pp-card-balance">
                    <button
                      type="button"
                      className={`pp-btn-estado ${estaAlDia ? "al-dia" : "debe"}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span>{estaAlDia ? "✓" : "!"}</span>
                      {estaAlDia ? "Al día" : "Debe"}
                    </button>
                  </div>
                </div>

                <div className="pp-info">
                  {p.medicacion && <span> Medicación: {p.medicacion}</span>}
                  {p.enfermedadSistemica && <span> | Enf. Sistémica: {p.enfermedadSistemica}</span>}
                </div>

                <div className="pp-info">
                  <span>
                    <b>Obra Social:</b> {p.obraSocial || "-"}
                  </span>
                </div>

                <div className="pp-indicadores">
                  <button
                    type="button"
                    className="pp-chip pp-chip-btn info"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/prestaciones`, { state: { fromPacientes: true } });
                    }}
                  >
                    💰{" "}
                    {p.ultima_prestacion
                      ? `Últ. pago ${formatDate(p.ultima_prestacion.fecha)} · ${p.ultima_prestacion.prestacion || "-"}`
                      : "Sin pagos"}
                  </button>
                  <button
                    type="button"
                    className="pp-chip pp-chip-btn info"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/seguimiento`, { state: { fromPacientes: true } });
                    }}
                  >
                    📅{" "}
                    {p.ultimo_seguimiento
                      ? `${formatDate(p.ultimo_seguimiento.fecha)} · ${truncate(p.ultimo_seguimiento.comentario)}`
                      : "Sin seguimiento"}
                  </button>
                </div>

                <div
                  className="pp-balance-texto"
                  style={{ marginTop: "8px", fontSize: "0.9rem", fontWeight: "500", color: "#555" }}
                >
                  <span>Total Prestaciones: ${totalCosto.toFixed(2)}</span>
                  <span style={{ margin: "0 6px" }}>|</span>
                  <span>Abonado: ${totalAbonado.toFixed(2)}</span>
                  <span style={{ margin: "0 6px" }}>|</span>
                  <span style={{ color: estaAlDia ? "#2e7d32" : "#d32f2f", fontWeight: "700" }}>
                    {estaAlDia ? "Saldo a favor: " : "Deuda: "}
                    ${Math.abs(balance).toFixed(2)}
                  </span>
                </div>

                <div className="pp-botones-inferiores">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/historia`, { state: { fromPacientes: true } });
                    }}
                    className={`boton-secundario boton-historia pp-accion-con-estado ${
                      p.tiene_historia ? "pp-accion-con-estado--ok" : "pp-accion-con-estado--off"
                    }`}
                  >
                    <span className="pp-accion-btn-main">
                      <span className="pp-icon">🩺</span> Historia Clínica
                    </span>
                    <span
                      className={`pp-accion-estado-inline ${p.tiene_historia ? "ok" : "off"}`}
                      aria-label={p.tiene_historia ? "Historia clínica cargada" : "Historia clínica pendiente"}
                    >
                      {p.tiene_historia ? "Cargada" : "Pendiente"}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImpresionPacienteId(p.id);
                    }}
                    className="boton-secundario boton-imprimir-hc"
                    title="Imprimir o guardar la historia clínica en PDF"
                  >
                    <span className="pp-icon">🖨️</span> Imprimir H.C.
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalAccesoPaciente(p);
                      setModalAccesoOpen(true);
                    }}
                    className="boton-secundario boton-acceso-hc"
                    title="Enviar acceso al portal de historia clínica por WhatsApp"
                  >
                    <span className="pp-icon">📱</span> Enviar acceso H.C.
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/prestaciones`, { state: { fromPacientes: true } });
                    }}
                    className="boton-secundario boton-abrir-pagos"
                  >
                    <span className="pp-icon">💰</span> Prestaciones y Pagos
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/seguimiento`, { state: { fromPacientes: true } });
                    }}
                    className="boton-secundario boton-seguimiento"
                  >
                    <span className="pp-icon">📅</span> Seguimiento
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/odontograma`, { state: { fromPacientes: true } });
                    }}
                    className={`boton-secundario boton-odontograma pp-accion-con-estado ${
                      p.tiene_odontograma ? "pp-accion-con-estado--ok" : "pp-accion-con-estado--off"
                    }`}
                  >
                    <span className="pp-accion-btn-main">
                      <span className="pp-icon">🦷</span> Odontograma
                    </span>
                    <span
                      className={`pp-accion-estado-inline ${p.tiene_odontograma ? "ok" : "off"}`}
                      aria-label={
                        p.tiene_odontograma
                          ? `Odontograma con ${p.odontograma_piezas_count || 0} piezas`
                          : "Odontograma pendiente"
                      }
                    >
                      {p.tiene_odontograma
                        ? `${p.odontograma_piezas_count || 0} piezas`
                        : "Pendiente"}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/rp`, { state: { fromPacientes: true } });
                    }}
                    className="boton-secundario boton-rp"
                  >
                    <span className="pp-icon">📋</span> RP
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/pacientes/${p.id}/rx`, { state: { fromPacientes: true } });
                    }}
                    className="boton-secundario boton-rx"
                  >
                    <span className="pp-icon">📷</span> RX
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {pacientes.length > 0 && pacientesFiltrados.length === 0 && (
          <div className="pp-card" style={{ marginTop: "8px" }}>
            <p className="pp-info" style={{ margin: 0 }}>
              No hay pacientes para el filtro actual.
            </p>
            <button type="button" className="boton-secundario" onClick={() => setBusqueda("")}>
              Limpiar búsqueda
            </button>
          </div>
        )}

        <PacienteFormModal
          open={modalOpen}
          paciente={isNewPatient ? null : modalPaciente}
          onClose={closeModal}
          onSaved={() => fetchPacientes()}
        />

        {modalAccesoOpen && (
          <EnviarAccesoModal
            open={modalAccesoOpen}
            paciente={modalAccesoPaciente}
            onClose={() => setModalAccesoOpen(false)}
            onConfirm={handleEnviarAcceso}
            loading={enviandoAcceso}
          />
        )}
      </div>

      {impresionPacienteId && createPortal(
        <HistoriaClinicaImpresion
          pacienteId={impresionPacienteId}
          onClose={() => setImpresionPacienteId(null)}
        />,
        document.body
      )}
    </div>
  );
}
